import { FC, useMemo, useState } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';
import NPICurrencyInput from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput';
import { useAdditionalTarrifs } from 'src/hooks';
import { ADDITIONAL_TARIFFS_PERSONAL_CARDS } from 'src/pages/AddTransactionPricingPage/sections/AdditionalTariffs';

import { CustomGridItem } from './components';
import { GridHeader } from './GridHeader';
import { AdditionalTariffsCard, AdditionalTariffsProps, DisplayOption, EditableField } from './types';

const AdditionalTariffs: FC<AdditionalTariffsProps> = ({
    name,
    editable,
    merchantApplication,
}: AdditionalTariffsProps) => {
    const [{ value: additionalTariffs }] = useField(name);
    const { editableFields } = useAdditionalTarrifs();
    const [applicationTariffs, _] = useState<Record<string, number>>({
        ...merchantApplication?.serviceRequirements?.transactionCharges?.additionalTariffs,
    });

    const displayOptions: DisplayOption[] = useMemo(() => {
        return Object.keys(additionalTariffs).map((additionalTariff: string) => {
            const additionalTarrifChargeDefault = ADDITIONAL_TARIFFS_PERSONAL_CARDS.find(
                (el: AdditionalTariffsCard) => {
                    return el.name === additionalTariff;
                }
            );

            const editableField = editableFields.find((field: EditableField) => {
                return field.name === additionalTariff;
            });

            const option = {
                name: additionalTariff,
                label: additionalTarrifChargeDefault?.heading ?? '',
                isEditable: !!editableField,
                min: editableField?.min,
                max: editableField?.max,
            };

            return option;
        });
    }, [additionalTariffs, editableFields]);

    return (
        <Box>
            <Typography fontSize="24px" color="#434341" marginBottom="8px">
                Additional Tariffs Table
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />
            <GridHeader additionalTarrifs />

            {displayOptions.map((option: DisplayOption) => {
                return (
                    <CustomGridItem
                        key={option.name}
                        item1={<Typography>{option.label}</Typography>}
                        item2={
                            <Flex gap="24px">
                                <NPICurrencyInput
                                    name={`${name}.${option.name}`}
                                    disabled={editable ? !option.isEditable : true}
                                    handleValue={(valueInCents: number) => {
                                        additionalTariffs[option.name] = valueInCents;
                                    }}
                                    defaultValueInCents={applicationTariffs[option.name] ?? additionalTariffs[option.name]}
                                    min={option?.min}
                                    max={option?.max}
                                />

                                <Flex sx={{ opacity: 0 }} />
                            </Flex>
                        }
                        columns={2}
                    />
                );
            })}
        </Box>
    );
};

export default AdditionalTariffs;
